var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { exportStageJSON } from './json';
import { validateSceneJSON } from './schema';
import { buildPdfFromJpegPages, createMinimalJpegStub, dataUrlToBytes, pdfToDataUrl, } from './pdf';
import { exportSvgDocument, snapshotToCanvas } from './snapshot';
import { EXPORT_MIME } from './exportTypes';
export { validateSceneJSON, parseAndValidateSceneJSON, formatJsonParseError, locateJsonError, formatValidationErrors, validateThemePack, listKnownSceneTypes, formatExpectedValues, formatInvalidValue, suggestClosest, UI_THEME_PRESETS, AUTOMOTIVE_THEME_PRESETS, registerKnownSceneTypes, } from './schema';
export { buildPdfFromJpegPages, pdfToDataUrl, dataUrlToBytes, createMinimalJpegStub } from './pdf';
var LIGHTDRAW_CDN = 'https://cdn.jsdelivr.net/npm/lightdraw/dist/lightdraw.min.js';
var LIGHTDRAW_CSS_CDN = 'https://cdn.jsdelivr.net/npm/lightdraw/dist/lightdraw.min.css';
/** Unified export API (Phase 10). */
export function exportApp(app, options) {
    var _a;
    var format = options.format;
    switch (format) {
        case 'json':
            return exportJson(app, options);
        case 'png':
            return exportRaster(app, options, 'image/png');
        case 'jpeg':
            return exportRaster(app, options, 'image/jpeg', (_a = options.quality) !== null && _a !== void 0 ? _a : 0.92);
        case 'svg':
            return exportSvg(app, options);
        case 'pdf':
            return exportPdf(app, options);
        case 'html':
            return exportHtml(app, options);
        default:
            return exportJson(app, options);
    }
}
/** Legacy export helper — prefer `exportApp`. */
export function exportScene(app, format, options) {
    if (options === void 0) { options = {}; }
    var result = exportApp(app, __assign(__assign({}, options), { format: format }));
    return result.data;
}
function exportJson(app, options) {
    var json = app.exportJSON({ compact: options.compact });
    if (options.validate) {
        var validation = validateSceneJSON(json);
        if (!validation.valid) {
            throw new Error("JSON validation failed: ".concat(validation.errors.join('; ')));
        }
    }
    return {
        format: 'json',
        data: json,
        mimeType: EXPORT_MIME.json,
    };
}
function exportRaster(app, options, mime, quality) {
    app.render();
    var snap = snapshotToCanvas(app, options);
    var dataUrl = mime === 'image/png'
        ? snap.canvas.toDataURL('image/png')
        : snap.canvas.toDataURL('image/jpeg', quality);
    return {
        format: mime === 'image/png' ? 'png' : 'jpeg',
        data: dataUrl,
        mimeType: mime,
        width: snap.canvas.width,
        height: snap.canvas.height,
    };
}
function exportSvg(app, options) {
    app.render();
    var xml = exportSvgDocument(app, options);
    var bounds = options.region
        ? undefined
        : { width: app.getSize().width, height: app.getSize().height };
    return {
        format: 'svg',
        data: xml,
        mimeType: EXPORT_MIME.svg,
        width: bounds === null || bounds === void 0 ? void 0 : bounds.width,
        height: bounds === null || bounds === void 0 ? void 0 : bounds.height,
    };
}
function exportPdf(app, options) {
    var _a, _b;
    app.render();
    var pageCount = Math.max(1, (_a = options.pages) !== null && _a !== void 0 ? _a : 1);
    var snap = snapshotToCanvas(app, options);
    var jpegUrl = snap.canvas.toDataURL('image/jpeg', (_b = options.quality) !== null && _b !== void 0 ? _b : 0.92);
    var jpegBytes = dataUrlToBytes(jpegUrl);
    if (jpegBytes.length < 4 || jpegBytes[0] !== 0xff) {
        jpegBytes = createMinimalJpegStub(snap.width * snap.pixelRatio, snap.height * snap.pixelRatio);
    }
    var pageW = snap.width * snap.pixelRatio;
    var pageH = snap.height * snap.pixelRatio;
    var pages = Array.from({ length: pageCount }, function () { return ({
        jpeg: jpegBytes,
        width: pageW,
        height: pageH,
    }); });
    var pdf = buildPdfFromJpegPages(pages);
    return {
        format: 'pdf',
        data: pdfToDataUrl(pdf),
        mimeType: EXPORT_MIME.pdf,
        width: pageW,
        height: pageH * pageCount,
    };
}
function exportHtml(app, options) {
    var _a;
    var json = exportStageJSON(app.stage, { compact: options.compact });
    if (options.validate) {
        var validation = validateSceneJSON(json);
        if (!validation.valid) {
            throw new Error("JSON validation failed: ".concat(validation.errors.join('; ')));
        }
    }
    var _b = app.getSize(), width = _b.width, height = _b.height;
    var bg = (_a = options.background) !== null && _a !== void 0 ? _a : app.getBackground();
    var sceneJson = JSON.stringify(json).replace(/</g, '\\u003c');
    var html = "<!DOCTYPE html>\n<html lang=\"en\">\n<head>\n  <meta charset=\"utf-8\">\n  <meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\">\n  <title>LightDraw Export</title>\n  <link rel=\"stylesheet\" href=\"".concat(LIGHTDRAW_CSS_CDN, "\">\n  <style>\n    body { margin: 0; font-family: system-ui, sans-serif; background: #0f172a; color: #e2e8f0; }\n    header { padding: 1rem; border-bottom: 1px solid #334155; }\n    #app { margin: 1rem auto; border: 1px solid #334155; border-radius: 8px; overflow: hidden; }\n  </style>\n</head>\n<body>\n  <header><h1>LightDraw Export</h1><p>Offline scene \u2014 requires network for LightDraw script on first load.</p></header>\n  <div id=\"app\"></div>\n  <script type=\"application/json\" id=\"scene\">").concat(sceneJson, "</script>\n  <script src=\"").concat(LIGHTDRAW_CDN, "\"></script>\n  <script>\n    (function () {\n      var scene = JSON.parse(document.getElementById('scene').textContent);\n      var app = LightDraw.createApp('#app', {\n        width: ").concat(width, ",\n        height: ").concat(height, ",\n        background: ").concat(JSON.stringify(bg), ",\n        renderer: 'html',\n        autoResize: false,\n      });\n      app.loadJSON(scene);\n      app.render();\n    })();\n  </script>\n</body>\n</html>");
    return {
        format: 'html',
        data: html,
        mimeType: EXPORT_MIME.html,
        width: width,
        height: height,
    };
}
/** Trigger browser download for export result. */
export function downloadExport(result, filename) {
    var ext = result.format;
    var name = filename !== null && filename !== void 0 ? filename : "lightdraw-export.".concat(ext);
    var blob;
    if (result.data instanceof Uint8Array) {
        blob = new Blob([new Uint8Array(result.data)], { type: result.mimeType });
    }
    else if (typeof result.data === 'string') {
        if (result.data.startsWith('data:')) {
            var bytes = dataUrlToBytes(result.data);
            blob = new Blob([new Uint8Array(bytes)], { type: result.mimeType });
        }
        else {
            blob = new Blob([result.data], { type: result.mimeType });
        }
    }
    else {
        blob = new Blob([JSON.stringify(result.data, null, 2)], { type: result.mimeType });
    }
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
}
/** Shallow scene comparison for round-trip tests. */
export function scenesEqual(a, b) {
    var e_1, _a;
    var _b, _c, _d, _e;
    if (a.type !== b.type)
        return false;
    var propsA = (_b = a.props) !== null && _b !== void 0 ? _b : {};
    var propsB = (_c = b.props) !== null && _c !== void 0 ? _c : {};
    var keysA = Object.keys(propsA).filter(function (k) { return !['id', 'metadata'].includes(k); });
    try {
        for (var keysA_1 = __values(keysA), keysA_1_1 = keysA_1.next(); !keysA_1_1.done; keysA_1_1 = keysA_1.next()) {
            var k = keysA_1_1.value;
            if (JSON.stringify(propsA[k]) !== JSON.stringify(propsB[k]))
                return false;
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (keysA_1_1 && !keysA_1_1.done && (_a = keysA_1.return)) _a.call(keysA_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var chA = (_d = a.children) !== null && _d !== void 0 ? _d : [];
    var chB = (_e = b.children) !== null && _e !== void 0 ? _e : [];
    if (chA.length !== chB.length)
        return false;
    for (var i = 0; i < chA.length; i++) {
        if (!scenesEqual(chA[i], chB[i]))
            return false;
    }
    return true;
}
/** Export region helper — crop to node. */
export function exportNodeRegion(node) {
    var _a = node.getBounds(), x = _a.x, y = _a.y, width = _a.width, height = _a.height;
    return { x: node.x + x, y: node.y + y, width: width, height: height };
}
